/**
 * 本文件包含两个组件：
 * 1. TestItemList - 测试项列表组件（含已通过标识）
 * 2. TestItemRow - 单个测试项行组件
 */

import React, { useCallback } from 'react';
import { Tooltip } from 'antd';
import classNames from 'classnames';
import { CheckOutlined } from '@ant-design/icons';
import { A11Y_ROLES } from '@/utils/a11y-attrs';
import { RoadTestItemDetail } from '@/fetch/bussiness/roadtest/types';
import { LocalAnswers, isAllQuestionsPassed } from '../utils';

export interface TestItemListProps {
  itemList: RoadTestItemDetail[];
  localAnswers: LocalAnswers;
  activeItemNo?: string;
  onSelect: (itemNo: string) => void;
}

/**
 * 测试项列表
 *
 * @param {*} {
 *   itemList,
 *   localAnswers,
 *   activeItemNo,
 *   onSelect,
 * }
 * @return {*}
 */
const TestItemList: React.FC<TestItemListProps> = ({
  itemList,
  localAnswers,
  activeItemNo,
  onSelect,
}) => {
  return (
    <div
      className="test-item-list"
      data-testid="test-item-list"
      {...A11Y_ROLES.navigation('测试项列表')}
    >
      {itemList.map((item) => {
        const answers = localAnswers[item.itemNo]?.questionList || [];
        const allPassed = isAllQuestionsPassed(answers);
        return (
          <TestItemRow
            key={item.itemNo}
            item={item}
            allPassed={allPassed}
            isActive={activeItemNo === item.itemNo}
            onSelect={onSelect}
          />
        );
      })}
    </div>
  );
};

interface TestItemRowProps {
  item: RoadTestItemDetail;
  allPassed: boolean;
  isActive: boolean;
  onSelect: (itemNo: string) => void;
}

/**
 * 单个测试项行组件
 *
 * @param {*} { item, allPassed, isActive, onSelect }
 * @return {*}
 */
const TestItemRow: React.FC<TestItemRowProps> = React.memo(
  ({ item, allPassed, isActive, onSelect }) => {
    const handleClick = useCallback(() => {
      onSelect(item.itemNo);
    }, [item.itemNo, onSelect]);

    return (
      <Tooltip title={item.itemName} placement="right">
        <div
          {...A11Y_ROLES.menuitem(item.itemName)}
          className={classNames('test-item', {
            'test-item-active': isActive,
            'test-item-passed-all': allPassed,
          })}
          onClick={handleClick}
          data-testid={`test-item-${item.itemNo}`}
          aria-current={isActive ? 'true' : undefined}
        >
          <span className="test-item-name">{item.itemName}</span>
          {allPassed && (
            <span
              className="test-item-passed-icon"
              data-testid={`test-item-passed-${item.itemNo}`}
            >
              <CheckOutlined />
            </span>
          )}
        </div>
      </Tooltip>
    );
  }
);
TestItemRow.displayName = 'TestItemRow';

export default React.memo(TestItemList);
